// src/pages/Projects.tsx

import React, { useEffect, useState } from 'react'
import { Button, Container, Form, Row, ButtonGroup, ToggleButton } from 'react-bootstrap'
import { useFetchUserProfileQuery } from '../services/authApi'
import ProjectCard from '../components/ProjectCard'
import { Project, ProjectAnnotationType } from '../types/models/project'
import { Model } from '../types/models/model'
import { useFetchProjectsQuery } from '../services/projectsApi'
import CPWizard from '../components/wizards/CPWizard'
import { Plus } from 'lucide-react'
import styled from 'styled-components'
import { ButtonProps } from 'react-bootstrap'
import { useAppDispatch } from '../store/hooks'
import { setValidationSource } from '../store/slices/datasetSlice'
import { setTrainingSource } from '../store/slices/datasetSlice'
import { setDatasetSources } from '../store/slices/datasetSlice'
import { clearFilterTags, clearSelectedProjectId } from '../store/slices/projectSlice'
import { setDeployedModels } from '../store/slices/projectSlice'
import { modelsApi } from '../services/modelsApi'

interface GenerateButtonProps extends ButtonProps {
  onClick?: () => void
  children?: React.ReactNode
}

const GenerateButton = styled(({ children, ...props }: GenerateButtonProps) => (
  <Button {...props}>{children}</Button>
)).attrs({
  as: 'button',
  type: 'button',
})`
  position: relative;
  width: auto;
  min-width: 200px;
  padding: 1rem 1.25rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  min-height: 64px;
  background: none !important;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px;
    background: linear-gradient(to bottom, #654358, #17092a, #2f0d64);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    background: #170928;
    opacity: 0.95;
  }

  .button-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    text-shadow: 0 0 10px rgba(199, 135, 246, 0.5);
    letter-spacing: 0.02em;
  }

  .gradient-overlay {
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    background: linear-gradient(to right, #170928, #1d0d33, #170928);
    opacity: 0.9;
    z-index: 1;
  }

  .gradient-overlay-2 {
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    background: linear-gradient(to bottom, rgba(101, 67, 88, 0.4), #1d0d33, rgba(47, 13, 100, 0.3));
    opacity: 0.8;
    z-index: 1;
  }

  .gradient-overlay-3 {
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    background: linear-gradient(
      to bottom right,
      rgba(199, 135, 246, 0.1),
      #1d0d33,
      rgba(42, 23, 54, 0.5)
    );
    z-index: 1;
  }

  .twinkle-overlay {
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    overflow: hidden;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(199, 135, 246, 0.1),
        rgba(199, 135, 246, 0.2),
        rgba(199, 135, 246, 0.1),
        transparent
      );
      background-size: 200% 100%;
      animation: shimmer 8s linear infinite;
    }
  }

  .inner-shadow {
    position: absolute;
    inset: 2px;
    border-radius: 10px;
    box-shadow: inset 0 0 15px rgba(199, 135, 246, 0.15);
    z-index: 1;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  svg {
    width: 20px;
    height: 20px;
    filter: drop-shadow(0 0 4px rgba(199, 135, 246, 0.5));
  }
`

const StyledToggleButton = styled(ToggleButton)<{
  $buttonType: ProjectAnnotationType
}>`
  ${(props) => {
    switch (props.$buttonType) {
      case 'classification':
        return `
          --bs-btn-hover-bg: #7C3AED;
          --bs-btn-hover-border-color: #6D28D9;
          --bs-btn-hover-color: #fff;
          --bs-btn-active-bg: #7C3AED;
          --bs-btn-active-border-color: #6D28D9;
          --bs-btn-active-color: #fff;
          --bs-btn-border-color: #7C3AED;
          --bs-btn-color: #7C3AED;
        `
      case 'bounding_box':
        return `
          --bs-btn-hover-bg: #059669;
          --bs-btn-hover-border-color: #10B981;
          --bs-btn-hover-color: #fff;
          --bs-btn-active-bg: #059669;
          --bs-btn-active-border-color: #10B981;
          --bs-btn-active-color: #fff;
          --bs-btn-border-color: #059669;
          --bs-btn-color: #059669;
        `
      case 'oriented_bounding_box':
        return `
          --bs-btn-hover-bg: #F59E0B;
          --bs-btn-hover-border-color: #D97706;
          --bs-btn-hover-color: #fff;
          --bs-btn-active-bg: #F59E0B;
          --bs-btn-active-border-color: #D97706;
          --bs-btn-border-color: #F59E0B;
          --bs-btn-color: #F59E0B;
        `
      default:
        return ''
    }
  }}
`

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1rem;
  padding: 1rem;

  &:has(*:hover) > * {
    opacity: 0.6;
    filter: brightness(0.6);
  }

  & > *:hover {
    opacity: 1 !important;
    filter: brightness(1) !important;
  }
`

const Projects: React.FC = () => {
  const dispatch = useAppDispatch()
  const { data: user } = useFetchUserProfileQuery()
  const [searchTerm, setSearchTerm] = useState('')
  const [projectTypeFilter, setProjectTypeFilter] = useState('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { data: projectsData } = useFetchProjectsQuery({})

  useEffect(() => {
    const fetchDeployedModels = async () => {
      if (!projectsData?.projects) return

      const allDeployedModels: Model[] = []

      for (const project of projectsData.projects) {
        try {
          const result = await dispatch(
            modelsApi.endpoints.fetchModels.initiate({ project_id: project.id })
          ).unwrap()

          if (result?.models) {
            const deployedModels = result.models.filter(
              (model) => model.deployment?.status.toLowerCase() === 'in_service'
            )
            allDeployedModels.push(...deployedModels)
          }
        } catch (error) {
          console.error(`Failed to fetch models for project ${project.id}:`, error)
        }
      }

      dispatch(setDeployedModels(allDeployedModels))
    }

    fetchDeployedModels()
  }, [projectsData, dispatch])

  useEffect(() => {
    dispatch(clearSelectedProjectId())
    localStorage.removeItem('lastSelectedProjectId')
    dispatch(setTrainingSource([]))
    dispatch(setValidationSource([]))
    dispatch(setDatasetSources([]))
    dispatch(clearFilterTags())
  }, [dispatch])

  const filteredProjects = [...(projectsData?.projects || [])]
    .sort((a, b) => new Date(b.last_accessed).getTime() - new Date(a.last_accessed).getTime())
    .filter((project: Project) => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType =
        projectTypeFilter === 'all' || project.annotation_type === projectTypeFilter
      return matchesSearch && matchesType
    })

  const projectTypes = [
    { name: 'All Projects', value: 'all' },
    { name: 'Classification', value: 'classification' },
    { name: 'Bounding Box', value: 'bounding_box' },
    { name: 'Oriented Box', value: 'oriented_bounding_box' },
  ]

  return (
    <Container fluid className="project-details">
      <Row style={{ height: '6rem' }}>
        <h1>Hi, {user?.first_name}</h1>
        <p className="pt-2">
          You have {projectsData?.projects?.length || 0} project
          {(projectsData?.projects?.length || 0) !== 1 ? 's' : ''} available
        </p>
      </Row>
      <Row style={{ height: '6vh' }} className="d-flex align-items-center">
        <div className="search-create-container">
          <div className="d-flex gap-3 align-items-center flex-grow-1">
            <Form.Control
              type="text"
              placeholder="Search Projects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <ButtonGroup>
              {projectTypes.map((type, idx) => (
                <StyledToggleButton
                  key={idx}
                  id={`radio-${idx}`}
                  type="radio"
                  name="projectType"
                  value={type.value}
                  checked={projectTypeFilter === type.value}
                  onChange={(e) => setProjectTypeFilter(e.currentTarget.value)}
                  $buttonType={type.value as ProjectAnnotationType}
                  variant={type.value === 'all' ? 'outline-secondary' : 'outline-light'}
                >
                  {type.name}
                </StyledToggleButton>
              ))}
            </ButtonGroup>
          </div>
          <GenerateButton onClick={() => setShowCreateModal(true)}>
            <div className="gradient-overlay" />
            <div className="gradient-overlay-2" />
            <div className="gradient-overlay-3" />
            <div className="twinkle-overlay" />
            <div className="inner-shadow" />
            <div className="button-content">
              <Plus size={20} />
              <span>New Project</span>
            </div>
          </GenerateButton>
        </div>
      </Row>
      <div style={{ maxHeight: '70vh', overflow: 'auto', width: '100%' }}>
        <ProjectsGrid>
          {filteredProjects &&
            filteredProjects.map((project: Project, index: number) => (
              <ProjectCard key={index} project={project} />
            ))}
        </ProjectsGrid>
      </div>
      <CPWizard show={showCreateModal} onHide={() => setShowCreateModal(false)} />
    </Container>
  )
}

export default Projects
